import React, { Component } from 'react';
import { Button, Form, Row, Col, Nav, Tab, Alert, Card } from 'react-bootstrap';
import {
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Chip,
    Box,
    OutlinedInput,
    IconButton,
    Tooltip,
    Autocomplete,
    TextField
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import toastr from 'toastr';
import { addReflexRule, updateReflexRule } from '../../../../services/clinicPortalServices/reflexRuleService';
import { getAllTests } from '../../../../services/clinicPortalServices/testService';
import { getAllAnalytes } from '../../../../services/clinicPortalServices/analyteService';
import { getAllMedications } from '../../../../services/clinicPortalServices/patientMedicationService';

import GridAutocomplete from '../../../common/GridAutocomplete';
const ruleTypes = [
    { value: 'result_based', label: 'Result Based' },
    { value: 'pattern_based', label: 'Pattern Based' },
    { value: 'combination', label: 'Combination' },
    { value: 'medication_based', label: 'Medication Based' },
    { value: 'toxicology_flag', label: 'Toxicology Flag Assignment' },
    { value: 'toxicology_outcome', label: 'Toxicology Outcome Determination' }
];

const operators = [
    { value: '>', label: 'Greater than' },
    { value: '>=', label: 'Greater than or equal' },
    { value: '<', label: 'Less than' },
    { value: '<=', label: 'Less than or equal' },
    { value: '==', label: 'Equal to' },
    { value: '!=', label: 'Not equal to' },
    { value: 'contains', label: 'Contains' },
    { value: 'not_contains', label: 'Does not contain' },
    { value: 'in_range', label: 'In range' },
    { value: 'out_of_range', label: 'Out of range' }
];

const toxicologyOperators = [
    { value: 'has_flag', label: 'Has Flag' },
    { value: 'has_outcome', label: 'Has Outcome' },
    { value: 'is_prescribed', label: 'Is Prescribed' },
    { value: 'not_prescribed', label: 'Not Prescribed' },
    { value: 'detected_and_prescribed', label: 'Detected AND Prescribed' },
    { value: 'detected_not_prescribed', label: 'Detected AND Not Prescribed' },
    { value: 'not_detected_prescribed', label: 'Not Detected AND Prescribed' },
    { value: 'not_detected_not_prescribed', label: 'Not Detected AND Not Prescribed' }
];

// Toxicology-specific flag types
const toxicologyFlags = [
    { value: 'ND', label: 'ND (Not Detected)' },
    { value: 'DET', label: 'DET (Detected)' },
    { value: 'BLOQ', label: 'BLOQ (Below Limit of Quantitation)' },
    { value: 'In Range', label: 'In Range' },
    { value: 'Out of Range', label: 'Out of Range' },
    { value: 'Pass', label: 'Pass' },
    { value: 'Fail', label: 'Fail' }
];

// Toxicology outcome types
const toxicologyOutcomes = [
    { value: 'CONSISTENT', label: 'CONSISTENT' },
    { value: 'INCONSISTENT', label: 'INCONSISTENT' },
    { value: 'Needs Review', label: 'Needs Review' }
];

const actionTypes = [
    { value: 'add_test', label: 'Add Test' },
    { value: 'cancel_test', label: 'Cancel Test' },
    { value: 'alert', label: 'Create Alert' },
    { value: 'comment', label: 'Add Comment' },
    { value: 'repeat_test', label: 'Repeat Test' },
    { value: 'assign_flag', label: 'Assign Flag' },
    { value: 'determine_outcome', label: 'Determine Outcome' }
];

const logicalOperators = [
    { value: 'AND', label: 'AND' },
    { value: 'OR', label: 'OR' }
];

export default class ReflexRuleDetails extends Component {
    constructor(props) {
        super(props);

        const rule = props.rule || {};
        const isNew = props.isNew || !rule._id;

        this.state = {
            isNew,
            isLoading: false,
            tests: [],
            analytes: [],
            filteredAnalytes: [], // Analytes filtered by selected test
            medications: [],
            testPanels: [],

            // Form fields
            ruleName: rule.name || rule.ruleName || '',
            description: rule.description || '',
            ruleType: rule.ruleType || 'result_based',
            priority: rule.priority || 50,

            // Trigger configuration
            triggerTestId: rule.triggerTestId?._id || rule.triggerTestId || '',
            selectedTriggerTest: null,
            // Extract triggerAnalyteId from first condition if available
            triggerAnalyteId:
                rule.triggerConditions && rule.triggerConditions.length > 0 && rule.triggerConditions[0].analyteId
                    ? rule.triggerConditions[0].analyteId._id || rule.triggerConditions[0].analyteId
                    : '',
            selectedTriggerAnalyte: null,

            // Conditions - transform from backend triggerConditions
            conditions: rule.triggerConditions
                ? rule.triggerConditions.map((tc) => ({
                      field: 'result',
                      operator: tc.operator,
                      value: tc.value || '',
                      unit: tc.unit || '',
                      analyteId: tc.analyteId?._id || tc.analyteId || null,
                      logicalOperator: 'AND'
                  }))
                : [
                      {
                          field: 'result',
                          operator: '>',
                          value: '',
                          unit: '',
                          analyteId: null,
                          logicalOperator: 'AND'
                      }
                  ],

            // Action configuration - transform from backend reflexActions
            action:
                rule.reflexActions && rule.reflexActions.length > 0
                    ? {
                          type: rule.reflexActions[0].actionType || 'add_test',
                          tests: rule.reflexActions
                              .filter((ra) => ra.actionType === 'add_test')
                              .map((ra) => (ra.testId?._id ? ra.testId._id : ra.testId)),
                          panels: [],
                          alertMessage: rule.reflexActions[0].message || '',
                          comment: rule.reflexActions[0].message || '',
                          flagType: 'warning',
                          toxicologyFlag: rule.reflexActions?.[0]?.toxicologyFlag || '',
                          toxicologyOutcome: rule.reflexActions?.[0]?.toxicologyOutcome || ''
                      }
                    : {
                          type: 'add_test',
                          tests: [],
                          panels: [],
                          alertMessage: '',
                          comment: '',
                          flagType: 'warning',
                          toxicologyFlag: '',
                          toxicologyOutcome: ''
                      },
            selectedActionTests: [],
            selectedActionPanels: [],

            // Medication-based rule fields - transform from medicationConditions
            medicationIds: rule.medicationConditions
                ? rule.medicationConditions.map((mc) => mc.medicationId?._id || mc.medicationId)
                : [],
            selectedMedications: [],
            timeWindow: rule.timeWindow || null,

            // Settings
            isActive: rule.isActive !== undefined ? rule.isActive : true,
            requiresApproval: rule.requiresApproval || false,
            notifyOnExecution: rule.notifyOnExecution || false,
            notes: rule.clinicalJustification || rule.notes || '',

            // Validation
            errors: {},
            activeTab: 'basic'
        };
    }

    componentDidMount() {
        // Load tests and analytes in parallel, then filter analytes if needed
        Promise.all([this.loadTests(), this.loadAnalytes()]).then(([testsData, analytesData]) => {
            // After both are loaded, filter analytes if a test is already selected
            if (testsData.selectedTest) {
                this.filterAnalytesForTest(testsData.selectedTest, analytesData);
            }
        });

        if (this.state.ruleType === 'medication_based') {
            this.loadMedications();
        }
    }

    loadTests = () => {
        return getAllTests()
            .then((response) => {
                const options = response.data.map((test) => ({
                    value: test._id,
                    label: test.name,
                    analyteIds: test.analyteIds || []
                }));
                this.setState({ tests: options });

                // Set selected trigger test if editing
                let selectedTest = null;
                if (this.props.rule?.triggerTestId) {
                    const testId = this.props.rule.triggerTestId._id || this.props.rule.triggerTestId;
                    selectedTest = options.find((opt) => opt.value === testId);
                    if (selectedTest) {
                        this.setState({
                            selectedTriggerTest: selectedTest,
                            triggerTestId: selectedTest.value
                        });
                    }
                }

                // Set selected action tests if editing
                if (this.props.rule?.action?.tests && this.props.rule.action.tests.length > 0) {
                    const selectedTests = options.filter((opt) =>
                        this.props.rule.action.tests.some((t) => t._id === opt.value)
                    );
                    this.setState({ selectedActionTests: selectedTests });
                }

                return { options, selectedTest };
            })
            .catch((error) => {
                console.error('Error loading tests:', error);
                toastr.error('Error loading tests');
                return { options: [], selectedTest: null };
            });
    };

    loadAnalytes = () => {
        return getAllAnalytes()
            .then((response) => {
                const options = response.data.map((analyte) => ({
                    value: analyte._id,
                    label: analyte.name
                }));
                this.setState({ analytes: options });

                // Check if we have a trigger analyte in the first condition
                if (this.props.rule?.triggerConditions && this.props.rule.triggerConditions.length > 0) {
                    const firstCondition = this.props.rule.triggerConditions[0];
                    if (firstCondition.analyteId) {
                        const analyteId = firstCondition.analyteId._id || firstCondition.analyteId;
                        const selected = options.find((opt) => opt.value === analyteId);
                        if (selected) {
                            this.setState({
                                selectedTriggerAnalyte: selected,
                                triggerAnalyteId: selected.value
                            });
                        }
                    }
                }

                return options;
            })
            .catch((error) => {
                console.error('Error loading analytes:', error);
                return [];
            });
    };

    loadMedications = () => {
        getAllMedications()
            .then((response) => {
                const options = response.data.map((med) => ({
                    value: med._id,
                    label: `${med.name} ${med.strength || ''}`
                }));
                this.setState({ medications: options });

                if (this.props.rule?.medicationConditions && this.props.rule.medicationConditions.length > 0) {
                    const selectedMeds = options.filter((opt) =>
                        this.props.rule.medicationConditions.find((item) => item?.medicationId?._id === opt.value)
                    );
                    this.setState({ selectedMedications: selectedMeds });
                }
            })
            .catch((error) => {
                console.error('Error loading medications:', error);
            });
    };

    handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        if (name.includes('.')) {
            const [parent, child] = name.split('.');
            this.setState((prevState) => ({
                [parent]: {
                    ...prevState[parent],
                    [child]: value
                }
            }));
        } else {
            this.setState({ [name]: type === 'checkbox' ? checked : value });
        }
    };

    handleTypeChange = (e) => {
        const newType = e.target.value;
        this.setState({ ruleType: newType });

        if (newType === 'medication_based' && this.state.medications.length === 0) {
            this.loadMedications();
        }
    };

    filterAnalytesForTest = (test, analytes = []) => {
        let analytesData = analytes;
        if (this.state.analytes.length > 0) {
            analytesData = this.state.analytes;
        }
        if (!test) {
            this.setState({ filteredAnalytes: analytesData });
            return;
        }

        if (!test.analyteIds || test.analyteIds.length === 0) {
            // If test has no analytes, show all analytes as a fallback
            this.setState({ filteredAnalytes: analytesData });
            return;
        }

        // Extract IDs from analyteIds (they could be populated objects or just ID strings)
        const analyteIdsSet = new Set(
            test.analyteIds.map((a) => {
                if (typeof a === 'object' && a !== null) {
                    return a._id || a.id;
                }
                return a;
            })
        );

        // Filter analytes to show only those that belong to the selected test
        const filtered = analytesData.filter((analyte) => analyteIdsSet.has(analyte.value));

        // If no analytes match, show all analytes as a fallback
        if (filtered.length === 0) {
            this.setState({ filteredAnalytes: analytesData });
        } else {
            this.setState({ filteredAnalytes: filtered });

            // If current trigger analyte is not in the filtered list, reset it
            if (this.state.triggerAnalyteId && !filtered.some((a) => a.value === this.state.triggerAnalyteId)) {
                this.setState({
                    triggerAnalyteId: '',
                    selectedTriggerAnalyte: null
                });
            }
        }
    };

    handleActionTestsChange = (event) => {
        const selectedIds = event.target.value;
        const selectedOptions = selectedIds.map((id) => this.state.tests.find((opt) => opt.value === id));
        this.setState({
            selectedActionTests: selectedOptions,
            action: {
                ...this.state.action,
                tests: selectedIds
            }
        });
    };

    handleMedicationsChange = (event) => {
        const selectedIds = event.target.value;
        const selectedOptions = selectedIds.map((id) => this.state.medications.find((opt) => opt.value === id));
        this.setState({
            selectedMedications: selectedOptions,
            medicationIds: selectedIds
        });
    };

    addCondition = () => {
        this.setState((prevState) => ({
            conditions: [
                ...prevState.conditions,
                {
                    field: 'result',
                    operator: '>',
                    value: '',
                    unit: '',
                    analyteId: null,
                    logicalOperator: 'AND'
                }
            ]
        }));
    };

    removeCondition = (index) => {
        if (this.state.conditions.length > 1) {
            this.setState((prevState) => ({
                conditions: prevState.conditions.filter((_, i) => i !== index)
            }));
        }
    };

    updateCondition = (index, field, value) => {
        this.setState((prevState) => ({
            conditions: prevState.conditions.map((cond, i) => (i === index ? { ...cond, [field]: value } : cond))
        }));
    };

    validateForm = () => {
        const errors = {};

        if (!this.state.ruleName) {
            errors.ruleName = 'Rule name is required';
        }

        if (!this.state.description) {
            errors.description = 'Description is required';
        }

        // For toxicology rules, require test but not necessarily analyte
        if (this.state.ruleType === 'toxicology_flag' || this.state.ruleType === 'toxicology_outcome') {
            if (!this.state.triggerTestId) {
                errors.triggerTestId = 'Trigger test is required';
            }
        } else if (this.state.ruleType !== 'medication_based') {
            if (!this.state.triggerTestId) {
                errors.triggerTestId = 'Trigger test is required';
            }

            if (!this.state.triggerAnalyteId) {
                errors.triggerAnalyteId = 'Trigger analyte is required for result-based rules';
            }

            // Check if we have at least one condition with a value
            // For result-based rules, having a trigger analyte is sufficient even without specific conditions
            const hasValidCondition = this.state.conditions.some((cond) => cond.value);

            if (!hasValidCondition && this.state.ruleType === 'result_based' && !this.state.triggerAnalyteId) {
                errors.conditions = 'At least one condition with a value is required, or select a trigger analyte';
            }
        } else {
            if (this.state.medicationIds.length === 0) {
                errors.medicationIds = 'At least one medication is required';
            }
        }

        // Validate conditions
        for (let i = 0; i < this.state.conditions.length; i++) {
            const cond = this.state.conditions[i];
            if (!cond.value) {
                errors[`condition_${i}`] = 'Condition value is required';
            }
        }

        // Validate action
        if (this.state.action.type === 'add_test' && this.state.action.tests.length === 0) {
            errors.actionTests = 'At least one test must be selected';
        } else if (this.state.action.type === 'assign_flag' && !this.state.action.toxicologyFlag) {
            errors.toxicologyFlag = 'A toxicology flag must be selected';
        } else if (this.state.action.type === 'determine_outcome' && !this.state.action.toxicologyOutcome) {
            errors.toxicologyOutcome = 'A toxicology outcome must be selected';
        }

        this.setState({ errors });
        return Object.keys(errors).length === 0;
    };

    handleSubmit = (e) => {
        e.preventDefault();

        if (!this.validateForm()) {
            toastr.error('Please fill in all required fields');
            return;
        }

        this.setState({ isLoading: true });

        // Transform conditions to match backend model
        const triggerConditions = this.state.conditions
            .map((cond) => ({
                analyteId: cond.analyteId || this.state.triggerAnalyteId,
                operator: cond.operator,
                value: cond.value,
                unit: cond.unit || undefined
            }))
            .filter((cond) => cond.analyteId); // Only include conditions with analyteId

        // If no conditions but triggerAnalyteId is set, add a default condition
        if (triggerConditions.length === 0 && this.state.triggerAnalyteId && this.state.ruleType === 'result_based') {
            triggerConditions.push({
                analyteId: this.state.triggerAnalyteId,
                operator: 'abnormal',
                value: null
            });
        }

        // Transform action to reflexActions array
        const reflexActions = [];
        if (this.state.action.type === 'add_test' && this.state.action.tests.length > 0) {
            this.state.action.tests.forEach((testId) => {
                reflexActions.push({
                    actionType: 'add_test',
                    testId: testId,
                    priority: 'routine',
                    autoApprove: false
                });
            });
        } else if (this.state.action.type === 'assign_flag') {
            reflexActions.push({
                actionType: this.state.action.type,
                toxicologyFlag: this.state.action.toxicologyFlag,
                priority: 'routine'
            });
        } else if (this.state.action.type === 'determine_outcome') {
            reflexActions.push({
                actionType: this.state.action.type,
                toxicologyOutcome: this.state.action.toxicologyOutcome,
                priority: 'routine'
            });
        } else {
            reflexActions.push({
                actionType: this.state.action.type,
                message: this.state.action.alertMessage || this.state.action.comment || '',
                priority: 'routine'
            });
        }

        const ruleData = {
            name: this.state.ruleName,
            description: this.state.description,
            ruleType: this.state.ruleType,
            priority: parseInt(this.state.priority),
            triggerTestId: this.state.ruleType !== 'medication_based' ? this.state.triggerTestId : null,
            triggerConditions: this.state.ruleType !== 'medication_based' ? triggerConditions : [],
            reflexActions: reflexActions,
            medicationConditions:
                this.state.ruleType === 'medication_based'
                    ? this.state.medicationIds.map((id) => ({ medicationId: id, presence: 'required' }))
                    : [],
            isActive: this.state.isActive,
            requiresApproval: this.state.requiresApproval,
            clinicalJustification: this.state.notes || ''
        };

        const savePromise = this.state.isNew
            ? addReflexRule(ruleData)
            : updateReflexRule(this.props.rule._id, ruleData);

        savePromise
            .then((response) => {
                toastr.success(`Rule ${this.state.isNew ? 'created' : 'updated'} successfully`);
                if (this.props.refreshGrid) {
                    this.props.refreshGrid();
                }
                this.props.handleClose();
            })
            .catch((error) => {
                console.error('Error saving rule:', error);
                toastr.error(`Error ${this.state.isNew ? 'creating' : 'updating'} rule`);
            })
            .finally(() => {
                this.setState({ isLoading: false });
            });
    };

    render() {
        const { errors } = this.state;

        return (
            <Form onSubmit={this.handleSubmit}>
                <Tab.Container activeKey={this.state.activeTab}>
                    <Nav variant="tabs" className="mb-3">
                        <Nav.Item>
                            <Nav.Link eventKey="basic" onClick={() => this.setState({ activeTab: 'basic' })}>
                                Basic Information
                            </Nav.Link>
                        </Nav.Item>
                        <Nav.Item>
                            <Nav.Link eventKey="conditions" onClick={() => this.setState({ activeTab: 'conditions' })}>
                                Conditions
                            </Nav.Link>
                        </Nav.Item>
                        <Nav.Item>
                            <Nav.Link eventKey="actions" onClick={() => this.setState({ activeTab: 'actions' })}>
                                Actions
                            </Nav.Link>
                        </Nav.Item>
                        <Nav.Item>
                            <Nav.Link eventKey="settings" onClick={() => this.setState({ activeTab: 'settings' })}>
                                Settings
                            </Nav.Link>
                        </Nav.Item>
                    </Nav>

                    <Tab.Content>
                        <Tab.Pane eventKey="basic">
                            <Row>
                                <Col md={12}>
                                    <Form.Group>
                                        <Form.Label>
                                            Rule Name <span className="text-danger">*</span>
                                        </Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="ruleName"
                                            value={this.state.ruleName}
                                            onChange={this.handleInputChange}
                                            isInvalid={!!errors.ruleName}
                                            placeholder="e.g., High Glucose Reflex to HbA1c"
                                        />
                                        <Form.Control.Feedback type="invalid">{errors.ruleName}</Form.Control.Feedback>
                                    </Form.Group>
                                </Col>

                                <Col md={12}>
                                    <Form.Group>
                                        <Form.Label>
                                            Description <span className="text-danger">*</span>
                                        </Form.Label>
                                        <Form.Control
                                            as="textarea"
                                            rows={3}
                                            name="description"
                                            value={this.state.description}
                                            onChange={this.handleInputChange}
                                            isInvalid={!!errors.description}
                                            placeholder="Describe what this rule does..."
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            {errors.description}
                                        </Form.Control.Feedback>
                                    </Form.Group>
                                </Col>

                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label>
                                            Rule Type <span className="text-danger">*</span>
                                        </Form.Label>
                                        <Form.Control
                                            as="select"
                                            name="ruleType"
                                            value={this.state.ruleType}
                                            onChange={this.handleTypeChange}
                                        >
                                            {ruleTypes.map((type) => (
                                                <option key={type.value} value={type.value}>
                                                    {type.label}
                                                </option>
                                            ))}
                                        </Form.Control>
                                    </Form.Group>
                                </Col>

                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label>Priority (1-100)</Form.Label>
                                        <Form.Control
                                            type="number"
                                            name="priority"
                                            value={this.state.priority}
                                            onChange={this.handleInputChange}
                                            min="1"
                                            max="100"
                                        />
                                        <Form.Text className="text-muted">
                                            Higher priority rules execute first
                                        </Form.Text>
                                    </Form.Group>
                                </Col>

                                {this.state.ruleType !== 'medication_based' && (
                                    <>
                                        <Col md={12}>
                                            <GridAutocomplete
                                                value={this.state.selectedTriggerTest}
                                                onChange={(event, newValue) => {
                                                    const selectedId = newValue ? newValue.value : '';
                                                    this.setState({
                                                        selectedTriggerTest: newValue,
                                                        triggerTestId: selectedId,
                                                        // Reset trigger analyte when test changes
                                                        triggerAnalyteId: '',
                                                        selectedTriggerAnalyte: null
                                                    });
                                                    if (newValue) {
                                                        this.filterAnalytesForTest(newValue);
                                                    } else {
                                                        this.setState({ filteredAnalytes: this.state.analytes });
                                                    }
                                                }}
                                                options={this.state.tests}
                                                getOptionLabel={(option) => option.label || ''}
                                                isOptionEqualToValue={(option, value) => option.value === value.value}
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        label={
                                                            <>
                                                                Trigger Test <span className="text-danger">*</span>
                                                            </>
                                                        }
                                                        error={!!errors.triggerTestId}
                                                        helperText={errors.triggerTestId}
                                                        margin="normal"
                                                        fullWidth
                                                    />
                                                )}
                                                filterOptions={(options, params) => {
                                                    const filtered = options.filter((option) =>
                                                        option.label
                                                            .toLowerCase()
                                                            .includes(params.inputValue.toLowerCase())
                                                    );
                                                    return filtered;
                                                }}
                                                sx={{ marginTop: 2 }}
                                            />
                                        </Col>

                                        <Col md={12}>
                                            <GridAutocomplete
                                                value={this.state.selectedTriggerAnalyte}
                                                onChange={(event, newValue) => {
                                                    const selectedId = newValue ? newValue.value : '';
                                                    this.setState({
                                                        selectedTriggerAnalyte: newValue,
                                                        triggerAnalyteId: selectedId
                                                    });
                                                }}
                                                options={this.state.filteredAnalytes}
                                                getOptionLabel={(option) => option.label || ''}
                                                isOptionEqualToValue={(option, value) => option.value === value.value}
                                                disabled={!this.state.triggerTestId}
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        label={
                                                            <>
                                                                Trigger Analyte <span className="text-danger">*</span>
                                                            </>
                                                        }
                                                        error={!!errors.triggerAnalyteId}
                                                        helperText={
                                                            errors.triggerAnalyteId ||
                                                            (!this.state.triggerTestId
                                                                ? 'Please select a trigger test first'
                                                                : '')
                                                        }
                                                        margin="normal"
                                                        fullWidth
                                                    />
                                                )}
                                                filterOptions={(options, params) => {
                                                    const filtered = options.filter((option) =>
                                                        option.label
                                                            .toLowerCase()
                                                            .includes(params.inputValue.toLowerCase())
                                                    );
                                                    return filtered;
                                                }}
                                                sx={{ marginTop: 2 }}
                                            />
                                        </Col>
                                    </>
                                )}

                                {this.state.ruleType === 'medication_based' && (
                                    <Col md={12}>
                                        <GridAutocomplete
                                            multiple
                                            value={this.state.selectedMedications}
                                            onChange={(event, newValue) => {
                                                const selectedIds = newValue.map((med) => med.value);
                                                this.setState({
                                                    selectedMedications: newValue,
                                                    medicationIds: selectedIds
                                                });
                                            }}
                                            options={this.state.medications}
                                            getOptionLabel={(option) => option.label || ''}
                                            isOptionEqualToValue={(option, value) => option.value === value.value}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    label={
                                                        <>
                                                            Medications <span className="text-danger">*</span>
                                                        </>
                                                    }
                                                    error={!!errors.medicationIds}
                                                    helperText={errors.medicationIds}
                                                    margin="normal"
                                                    fullWidth
                                                />
                                            )}
                                            renderTags={(value, getTagProps) =>
                                                value.map((option, index) => (
                                                    <Chip
                                                        label={option.label}
                                                        {...getTagProps({ index })}
                                                        size="small"
                                                    />
                                                ))
                                            }
                                            filterOptions={(options, params) => {
                                                const filtered = options.filter((option) =>
                                                    option.label.toLowerCase().includes(params.inputValue.toLowerCase())
                                                );
                                                return filtered;
                                            }}
                                            sx={{ marginTop: 2 }}
                                        />
                                    </Col>
                                )}
                            </Row>
                        </Tab.Pane>

                        <Tab.Pane eventKey="conditions">
                            <div className="mb-3">
                                <h5>Rule Conditions</h5>
                                <p className="text-muted">
                                    Define the conditions that must be met for this rule to trigger
                                </p>
                                {errors.conditions && <Alert variant="danger">{errors.conditions}</Alert>}
                            </div>

                            {this.state.conditions.map((condition, index) => (
                                <Card key={index} className="mb-3">
                                    <Card.Body>
                                        <Row>
                                            {index > 0 && (
                                                <Col md={12} className="mb-3">
                                                    <Form.Control
                                                        as="select"
                                                        value={condition.logicalOperator}
                                                        onChange={(e) =>
                                                            this.updateCondition(
                                                                index,
                                                                'logicalOperator',
                                                                e.target.value
                                                            )
                                                        }
                                                    >
                                                        {logicalOperators.map((op) => (
                                                            <option key={op.value} value={op.value}>
                                                                {op.label}
                                                            </option>
                                                        ))}
                                                    </Form.Control>
                                                </Col>
                                            )}

                                            <Col md={3}>
                                                <Form.Group>
                                                    <Form.Label>Field</Form.Label>
                                                    <Form.Control
                                                        as="select"
                                                        value={condition.field}
                                                        onChange={(e) =>
                                                            this.updateCondition(index, 'field', e.target.value)
                                                        }
                                                    >
                                                        {this.state.ruleType === 'toxicology_flag' ? (
                                                            <>
                                                                <option value="value">Test Value</option>
                                                                <option value="lowLimit">Low Limit Threshold</option>
                                                                <option value="criticalLow">Critical Low</option>
                                                            </>
                                                        ) : this.state.ruleType === 'toxicology_outcome' ? (
                                                            <>
                                                                <option value="flag">Flag Status</option>
                                                                <option value="medication">Medication Match</option>
                                                            </>
                                                        ) : (
                                                            <>
                                                                <option value="result">Result Value</option>
                                                                <option value="flag">Result Flag</option>
                                                                <option value="status">Result Status</option>
                                                            </>
                                                        )}
                                                    </Form.Control>
                                                </Form.Group>
                                            </Col>

                                            <Col md={3}>
                                                <Form.Group>
                                                    <Form.Label>Operator</Form.Label>
                                                    <Form.Control
                                                        as="select"
                                                        value={condition.operator}
                                                        onChange={(e) =>
                                                            this.updateCondition(index, 'operator', e.target.value)
                                                        }
                                                    >
                                                        {(this.state.ruleType === 'toxicology_flag' ||
                                                        this.state.ruleType === 'toxicology_outcome'
                                                            ? toxicologyOperators
                                                            : operators
                                                        ).map((op) => (
                                                            <option key={op.value} value={op.value}>
                                                                {op.label}
                                                            </option>
                                                        ))}
                                                    </Form.Control>
                                                </Form.Group>
                                            </Col>

                                            <Col md={3}>
                                                <Form.Group>
                                                    <Form.Label>
                                                        Value <span className="text-danger">*</span>
                                                    </Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        value={condition.value}
                                                        onChange={(e) =>
                                                            this.updateCondition(index, 'value', e.target.value)
                                                        }
                                                        isInvalid={!!errors[`condition_${index}`]}
                                                        placeholder="Enter value"
                                                    />
                                                    <Form.Control.Feedback type="invalid">
                                                        {errors[`condition_${index}`]}
                                                    </Form.Control.Feedback>
                                                </Form.Group>
                                            </Col>

                                            <Col md={2}>
                                                <Form.Group>
                                                    <Form.Label>Unit</Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        value={condition.unit}
                                                        onChange={(e) =>
                                                            this.updateCondition(index, 'unit', e.target.value)
                                                        }
                                                        placeholder="e.g., mg/dL"
                                                    />
                                                </Form.Group>
                                            </Col>

                                            <Col
                                                md={1}
                                                className="d-flex align-items-center"
                                                style={{ paddingTop: '10px' }}
                                            >
                                                {this.state.conditions.length > 1 && (
                                                    <Tooltip title="Remove condition">
                                                        <IconButton
                                                            color="error"
                                                            size="small"
                                                            onClick={() => this.removeCondition(index)}
                                                            sx={{
                                                                '&:hover': {
                                                                    backgroundColor: 'rgba(211, 47, 47, 0.04)'
                                                                }
                                                            }}
                                                        >
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                            </Col>
                                        </Row>
                                    </Card.Body>
                                </Card>
                            ))}

                            <Button variant="secondary" onClick={this.addCondition}>
                                <i className="fas fa-plus"></i> Add Condition
                            </Button>
                        </Tab.Pane>

                        <Tab.Pane eventKey="actions">
                            <Row>
                                <Col md={12}>
                                    <Form.Group>
                                        <Form.Label>Action Type</Form.Label>
                                        <Form.Control
                                            as="select"
                                            name="action.type"
                                            value={this.state.action.type}
                                            onChange={this.handleInputChange}
                                        >
                                            {actionTypes.map((type) => (
                                                <option key={type.value} value={type.value}>
                                                    {type.label}
                                                </option>
                                            ))}
                                        </Form.Control>
                                    </Form.Group>
                                </Col>

                                {this.state.action.type === 'add_test' && (
                                    <Col md={12}>
                                        <FormControl fullWidth error={!!errors.actionTests} margin="normal">
                                            <InputLabel id="action-tests-label">
                                                Tests to Add <span className="text-danger">*</span>
                                            </InputLabel>
                                            <Select
                                                labelId="action-tests-label"
                                                multiple
                                                value={this.state.action.tests}
                                                onChange={this.handleActionTestsChange}
                                                input={<OutlinedInput label="Tests to Add" />}
                                                renderValue={(selected) => (
                                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                        {selected.map((value) => {
                                                            const test = this.state.tests.find(
                                                                (t) => t.value === value
                                                            );
                                                            return (
                                                                <Chip key={value} label={test ? test.label : value} />
                                                            );
                                                        })}
                                                    </Box>
                                                )}
                                            >
                                                {this.state.tests.map((test) => (
                                                    <MenuItem key={test.value} value={test.value}>
                                                        {test.label}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                            {errors.actionTests && (
                                                <div className="text-danger mt-1" style={{ fontSize: '0.875em' }}>
                                                    {errors.actionTests}
                                                </div>
                                            )}
                                        </FormControl>
                                    </Col>
                                )}

                                {this.state.action.type === 'alert' && (
                                    <Col md={12}>
                                        <Form.Group>
                                            <Form.Label>Alert Message</Form.Label>
                                            <Form.Control
                                                as="textarea"
                                                rows={3}
                                                name="action.alertMessage"
                                                value={this.state.action.alertMessage}
                                                onChange={this.handleInputChange}
                                                placeholder="Enter alert message..."
                                            />
                                        </Form.Group>
                                    </Col>
                                )}

                                {this.state.action.type === 'comment' && (
                                    <Col md={12}>
                                        <Form.Group>
                                            <Form.Label>Comment Text</Form.Label>
                                            <Form.Control
                                                as="textarea"
                                                rows={3}
                                                name="action.comment"
                                                value={this.state.action.comment}
                                                onChange={this.handleInputChange}
                                                placeholder="Enter comment to add..."
                                            />
                                        </Form.Group>
                                    </Col>
                                )}

                                {this.state.action.type === 'flag' && (
                                    <Col md={12}>
                                        <Form.Group>
                                            <Form.Label>Flag Type</Form.Label>
                                            <Form.Control
                                                as="select"
                                                name="action.flagType"
                                                value={this.state.action.flagType}
                                                onChange={this.handleInputChange}
                                            >
                                                <option value="warning">Warning</option>
                                                <option value="critical">Critical</option>
                                                <option value="info">Information</option>
                                            </Form.Control>
                                        </Form.Group>
                                    </Col>
                                )}

                                {this.state.action.type === 'assign_flag' && (
                                    <Col md={12}>
                                        <Form.Group>
                                            <Form.Label>Toxicology Flag</Form.Label>
                                            <Form.Control
                                                as="select"
                                                name="action.toxicologyFlag"
                                                value={this.state.action.toxicologyFlag || ''}
                                                onChange={this.handleInputChange}
                                            >
                                                <option value="">Select Flag</option>
                                                {toxicologyFlags.map((flag) => (
                                                    <option key={flag.value} value={flag.value}>
                                                        {flag.label}
                                                    </option>
                                                ))}
                                            </Form.Control>
                                        </Form.Group>
                                    </Col>
                                )}

                                {this.state.action.type === 'determine_outcome' && (
                                    <Col md={12}>
                                        <Form.Group>
                                            <Form.Label>Toxicology Outcome</Form.Label>
                                            <Form.Control
                                                as="select"
                                                name="action.toxicologyOutcome"
                                                value={this.state.action.toxicologyOutcome || ''}
                                                onChange={this.handleInputChange}
                                            >
                                                <option value="">Select Outcome</option>
                                                {toxicologyOutcomes.map((outcome) => (
                                                    <option key={outcome.value} value={outcome.value}>
                                                        {outcome.label}
                                                    </option>
                                                ))}
                                            </Form.Control>
                                        </Form.Group>
                                    </Col>
                                )}
                            </Row>
                        </Tab.Pane>

                        <Tab.Pane eventKey="settings">
                            <Row>
                                <Col md={12}>
                                    <Form.Group>
                                        <Form.Check
                                            type="switch"
                                            id="active-switch"
                                            label="Rule Active"
                                            name="isActive"
                                            checked={this.state.isActive}
                                            onChange={this.handleInputChange}
                                        />
                                    </Form.Group>
                                </Col>

                                <Col md={12}>
                                    <Form.Group>
                                        <Form.Check
                                            type="switch"
                                            id="approval-switch"
                                            label="Requires Approval Before Execution"
                                            name="requiresApproval"
                                            checked={this.state.requiresApproval}
                                            onChange={this.handleInputChange}
                                        />
                                    </Form.Group>
                                </Col>

                                <Col md={12}>
                                    <Form.Group>
                                        <Form.Check
                                            type="switch"
                                            id="notify-switch"
                                            label="Send Notification When Executed"
                                            name="notifyOnExecution"
                                            checked={this.state.notifyOnExecution}
                                            onChange={this.handleInputChange}
                                        />
                                    </Form.Group>
                                </Col>

                                <Col md={12}>
                                    <Form.Group>
                                        <Form.Label>Notes</Form.Label>
                                        <Form.Control
                                            as="textarea"
                                            rows={3}
                                            name="notes"
                                            value={this.state.notes}
                                            onChange={this.handleInputChange}
                                            placeholder="Additional notes or documentation..."
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>
                        </Tab.Pane>
                    </Tab.Content>
                </Tab.Container>

                <div className="mt-3 d-flex justify-content-between">
                    <Button variant="secondary" onClick={this.props.handleClose}>
                        Cancel
                    </Button>
                    <Button variant="primary" type="submit" disabled={this.state.isLoading}>
                        {this.state.isLoading ? 'Saving...' : this.state.isNew ? 'Create Rule' : 'Update Rule'}
                    </Button>
                </div>
            </Form>
        );
    }
}
